import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Image,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Text from '../../components/common/Text';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../hooks/useAuth';
import TextField from '../../components/common/TextField';
import Button from '../../components/common/Button';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useTheme } from '../../hooks/useTheme';

const schema = z.object({
  email: z.string().email('Invalid email address'),
});

type FormData = z.infer<typeof schema>;

const ForgotPasswordScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { requestPasswordReset } = useAuth();
  const { isMobile, screenWidth } = useResponsiveLayout();
  const { colors } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const logoSize = isMobile ? 40 : isTablet ? 50 : 60;
  const formPadding = isMobile ? 24 : isTablet ? 40 : 50;

  const { control, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    try {
      await requestPasswordReset(data.email);
      setSent(true);
      setTimeout(() => navigation.navigate('Login'), 3000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveContainer
      style={[styles.container, { backgroundColor: colors.authBackground }]}
    >
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContentContainer}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View
            style={[
              styles.contentContainer,
              !isMobile && styles.webContentContainer,
            ]}
          >
            <View
              style={[
                styles.formContainer,
                {
                  backgroundColor: colors.card,
                  shadowColor: colors.border,
                  borderColor: colors.borderLight,
                  paddingHorizontal: formPadding,
                },
              ]}
            >
              <View style={styles.formHeader}>
                <TouchableOpacity
                  onPress={() => navigation.navigate('Landing')}
                  style={styles.logoContainer}
                >
                  <Image
                    source={require('../../../assets/logo-square.png')}
                    style={{ width: logoSize, height: logoSize }}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
                <Text
                  size="md"
                  weight="bold"
                  style={[styles.formTitle, { color: colors.primary }]}
                >
                  Forgot Password
                </Text>
                <Text
                  size="sm"
                  style={[styles.formSubtitle, { color: colors.secondary }]}
                >
                  Enter your email and we&apos;ll send you a link to reset your
                  password.
                </Text>
              </View>

              {sent ? (
                <View style={styles.sentMessage}>
                  <Text size="sm" style={{ color: colors.text }}>
                    If an account exists for that email, we&apos;ve sent a reset
                    link. Check your inbox and spam folder.
                  </Text>
                  <Text
                    size="sm"
                    style={[styles.backLink, { color: colors.primary }]}
                    onPress={() => navigation.navigate('Login')}
                  >
                    Back to sign in
                  </Text>
                </View>
              ) : (
                <View style={styles.form}>
                  <Controller
                    control={control}
                    name="email"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextField
                        label="Email"
                        placeholder="Enter your email"
                        keyboardType="email-address"
                        autoCapitalize="none"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        error={error || undefined}
                      />
                    )}
                  />
                  {error && (
                    <View
                      style={[
                        styles.errorContainer,
                        { backgroundColor: colors.errorBackground },
                      ]}
                    >
                      <Text
                        size="sm"
                        weight="medium"
                        style={[styles.errorText, { color: colors.errorText }]}
                      >
                        {error}
                      </Text>
                    </View>
                  )}
                  <Button
                    title="Send reset link"
                    onPress={handleSubmit(onSubmit)}
                    loading={loading}
                    style={styles.button}
                    trackClick={true}
                    trackingLocation="forgot-password-screen"
                    trackingData={{
                      button_name: 'Send reset link',
                      button_type: 'send_reset_link',
                      screen: 'forgot_password',
                    }}
                  />
                </View>
              )}

              <View style={styles.footer}>
                <TouchableOpacity onPress={() => navigation.navigate('Login')}>
                  <Text
                    size="md"
                    weight="semibold"
                    style={{ color: colors.primary }}
                  >
                    Back to sign in
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  keyboardAvoidingView: { flex: 1, zIndex: 10 },
  scrollView: { flex: 1 },
  scrollContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
    minHeight: Platform.OS === 'web' ? undefined : '100%',
  },
  contentContainer: { maxWidth: 440, width: '100%', alignSelf: 'center' },
  webContentContainer: { maxWidth: 440 },
  formContainer: {
    borderRadius: 16,
    borderWidth: 1,
    paddingVertical: 28,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  formHeader: { alignItems: 'center', marginBottom: 24 },
  logoContainer: { marginBottom: 16 },
  formTitle: { marginBottom: 8 },
  formSubtitle: { textAlign: 'center', paddingHorizontal: 8 },
  form: { gap: 16 },
  button: { marginTop: 8 },
  errorContainer: { padding: 12, borderRadius: 8, marginTop: 8 },
  errorText: {},
  sentMessage: { paddingVertical: 16 },
  backLink: { marginTop: 16 },
  footer: { marginTop: 24, alignItems: 'center' },
});

export default ForgotPasswordScreen;
